<template>
  <v-card outlined class="pa-0">
    <v-card-title>
      <div style="width: 60%"></div>
      <v-spacer></v-spacer>
      <v-text-field
        dense
        hide-details
        append-icon="mdi-magnify"
        v-model="search"
        label="Search Document"
        single-line
      ></v-text-field>
    </v-card-title>
    <v-card-text>
    <v-data-table
      dense
      :loading="isLoading"
      :search="search"
      :headers="headers"
      :items="documents"
      :items-per-page="5"
      :footer-props="{
        'items-per-page-options': [5, 10, 20, -1]
      }"
    >
      <template v-slot:[`item.Document_Name`]="{ item, index }">
        <v-text-field
          v-if="editMode && currentRowIndex === index"
          :readonly="!editingDocument.File && fileWasChangedEditMode"
          v-model="editingDocument.Document_Name"
          dense
          persistent-hint
          :disabled="isLoading"
          :hint="editingDocument.File ? convertToKB(editingDocument.File.size) : 'No file selected'"
          :rules="[rules.requiredFile]"
        >
          <template v-slot:append>
            <v-file-input
              hide-input
              hide-details
              :disabled="isLoading"
              dense
              @change="onFileChangeEditMode"
              v-model="editingDocument.File"
              accept="application/pdf, image/*"
            >
            </v-file-input>
          </template>
        </v-text-field>
        <a @click="downloadDocument(item)" v-else>{{ item.Document_Name }}</a>
      </template>
      <template v-slot:[`item.Updated_By_Employee`]="{ item, index }">
        <span v-if="editMode && currentRowIndex === index">
          {{ user.full_name }}
        </span>
        <span v-else>{{ item.Updated_By_Employee }}</span>
      </template>
      <template v-slot:[`item.Type`]="{ item, index }">
        <v-select
          v-if="editMode && currentRowIndex === index"
          v-model="editingDocument.Document_Type_ID"
          :items="documentsTypes"
          item-text="Name"
          hide-details
          :disabled="isLoading"
          dense
          :rules="[rules.requitedText]"
          item-value="Type_ID"
        >
        </v-select>
        <span v-else>{{ item.Type }}</span>
      </template>
      <template v-slot:[`item.Updated_Date`]="{ item, index }">
        <span v-if="editMode && currentRowIndex === index"></span>
        <span v-else>{{ item.Updated_Date }}</span>
      </template>
      <template v-slot:[`item.Notes`]="{ item, index }">
        <v-textarea
          v-if="editMode && currentRowIndex === index"
          v-model="editingDocument.Notes"
          rows="1"
          :disabled="isLoading"
          auto-grow
          hide-details
          dense
        ></v-textarea>
        <span v-else>{{ item.Notes }}</span>
      </template>
      <template v-slot:[`item.Actions`]="{ item, index }">
        <v-btn
          :disabled="addNewMode || editMode && (currentRowIndex !== index) || (currentRowIndex === index && !isEditedDocumentReady) || isLoading"
          :loading="editMode && currentRowIndex === index && isLoading"
          :color="editMode && currentRowIndex === index ? 'success' : 'primary'"
          small
          icon
          @click="onEditClick(item, index)"
        >
          <v-icon>
            {{ currentRowIndex === index ? 'mdi-check-circle' : 'mdi-pencil-circle' }}
          </v-icon>
        </v-btn>
        <v-btn
          small
          icon
          color="red darken-2"
          v-if="index !== currentRowIndex"
          :disabled="editMode || addNewMode || isLoading"
          @click="onDeleteClick(item)"
        >
          <v-icon>mdi-delete-circle</v-icon>
        </v-btn>
        <v-btn
          v-if="editMode && currentRowIndex === index"
          :disabled="editMode && isLoading"
          color="primary"
          small
          icon
          @click="cancelEditDocument"
        >
          <v-icon>mdi-close-circle</v-icon>
        </v-btn>
      </template>
      <template v-slot:[`body.append`]>
        <td
          class="px-2 text-no-wrap"
          :class="{
            'v-data-table__divider': addNewMode
          }"
        >
          <v-text-field
            v-if="addNewMode"
            :readonly="!newDocument.File"
            v-model="newDocument.Document_Name"
            dense
            persistent-hint
            :disabled="isLoading"
            :hint="newDocument.File ? convertToKB(newDocument.File.size) : 'No file selected'"
            :rules="[rules.requiredFile]"
          >
            <template v-slot:append>
              <v-file-input
                hide-input
                hide-details
                dense
                @change="onFileChangeAddMode"
                v-model="newDocument.File"
                :disabled="isLoading"
                :rules="[rules.requitedText]"
                accept="application/pdf, image/*"
              >
              </v-file-input>
            </template>
          </v-text-field>
        </td>
        <td
          class="px-2 text-no-wrap"
          :class="{
            'v-data-table__divider': addNewMode
          }"
        >
          <span v-if="addNewMode">{{ user.full_name }}</span>
        </td>
        <td
          class="px-2 text-no-wrap"
          :class="{
            'v-data-table__divider': addNewMode
          }"
        >
        </td>
        <td
          class="px-2 text-no-wrap"
          :class="{
            'v-data-table__divider': addNewMode
          }"
        >
          <v-select
            v-if="addNewMode"
            v-model="newDocument.Document_Type_ID"
            :items="documentsTypes"
            item-text="Name"
            hide-details
            dense
            :disabled="isLoading"
            :rules="[rules.requitedText]"
            item-value="Type_ID"
          >
          </v-select>
        </td>
        <td
          class="px-2 text-no-wrap"
          :class="{
            'v-data-table__divider': addNewMode
          }"
        >
        </td>
        <td
          class="px-2 text-no-wrap"
          :class="{
            'v-data-table__divider': addNewMode
          }"
        >
        </td>
        <td
          class="px-2 text-no-wrap"
          :class="{
            'v-data-table__divider': addNewMode
          }"
        >
          <v-textarea
            v-if="addNewMode"
            v-model="newDocument.Notes"
            rows="1"
            auto-grow
            hide-details
            dense
            :disabled="isLoading"
          ></v-textarea>
        </td>
        <td class="text-center text-no-wrap">
          <v-btn
            :disabled="(addNewMode && !isNewDocumentReady) || editMode || (!editMode || !addNewMode) && isLoading"
            :loading="addNewMode && isLoading"
            icon
            color="success"
            small
            @click.prevent="addNewDocument"
          >
            <v-icon>
              {{ addNewMode ? 'mdi-check-circle' : 'mdi-plus-circle' }}
            </v-icon>
          </v-btn>
          <v-btn
            v-if="addNewMode"
            :disabled="isLoading"
            icon
            color="primary"
            small
            @click.prevent="cancelAddNewDocument"
          >
            <v-icon>mdi-close-circle</v-icon>
          </v-btn>
        </td>
      </template>
    </v-data-table>
    </v-card-text>
    <v-dialog
      v-model="deleteDocumentDialog"
      max-width="500"
    >
      <v-card>
        <v-card-title>
          Are you sure?
        </v-card-title>
        <v-card-text>
          <v-textarea
            spellcheck="false"
            v-model="deleteNotes"
            label="Deleted notes"
          ></v-textarea>
        </v-card-text>
        <v-card-actions>
          <v-btn color="primary" small outlined @click="cancelDeleteDocument">Cancel</v-btn>
          <v-spacer></v-spacer>
          <v-btn :disabled="!deleteNotes" color="red darken-2 white--text" small @click="deleteDocument">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-card>
</template>
<script>
import { getApiErrorMessage } from '@/utils/apiError'
import { formatLocalDate } from '@/utils/dateUtils'
export default {
  name: 'EmployeeDocuments',
  components: {},
  props: {
    employee: {
      required: false
    },
    user: {
      required: false
    }
  },
  data () {
    return {
      addNewMode: false,
      currentRowIndex: null,
      documents: [],
      documentsTypes: [],
      deleteDocumentDialog: false,
      deleteNotes: '',
      documentIDToDelete: null,
      documentOldName: null,
      documentIDToEdit: null,
      downloadLink: document.createElement('a'),
      search: '',
      editMode: false,
      editingDocument: null,
      isLoading: false,
      fileWasChangedEditMode: false,
      newDocument: null,
      rules: {
        requiredFile: v => (v && !!v.toString().trim()) || 'The file name is required',
        requitedText: v => !!v || 'Required'
      },
      headers: [
        {
          text: 'Name',
          value: 'Document_Name',
          divider: true,
          width: '17%',
          cellClass: 'px-2',
          class: 'px-2 text-no-wrap'
        },
        {
          text: 'Uploaded by',
          value: 'Uploaded_By_Employee',
          divider: true,
          width: '12%',
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap'
        },
        {
          text: 'Updated by',
          value: 'Updated_By_Employee',
          divider: true,
          width: '12%',
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap'
        },
        {
          text: 'Type',
          value: 'Type',
          divider: true,
          width: '12%',
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap'
        },
        {
          text: 'Uploaded Date',
          value: 'Uploaded_Date',
          divider: true,
          width: '8%',
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap'
        },
        {
          text: 'Updated Date',
          value: 'Updated_Date',
          divider: true,
          width: '8%',
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap'
        },
        {
          text: 'Notes',
          value: 'Notes',
          divider: true,
          cellClass: 'px-2',
          class: 'px-2 text-no-wrap'
        },
        {
          text: '#',
          width: 100,
          sortable: false,
          align: 'center',
          cellClass: 'px-2 text-no-wrap',
          class: 'px-2 text-no-wrap',
          value: 'Actions'
        }
      ]
    }
  },
  mounted () {
    this.getEmployeeDocuments()
    this.getEmployeeDocumentsTypes()
  },
  computed: {
    isNewDocumentReady () {
      return this.addNewMode && this.newDocument && this.newDocument.Document_Name?.trim().length &&
        this.newDocument.Document_Of_Employee_ID && this.newDocument.Uploaded_By_Employee_ID &&
        this.newDocument.File && this.newDocument.Document_Type_ID
    },
    isEditedDocumentReady () {
      return this.editMode && this.editingDocument && this.editingDocument.Document_Name?.trim().length &&
        this.editingDocument.Document_Type_ID
    }
  },
  methods: {
    convertToKB (bites) {
      return (bites / 1024).toFixed(2) + ' KB'
    },
    allowTableSorting (boolean) {
      for (const header of this.headers) {
        if (header.value !== 'Actions') {
          header.sortable = boolean
        }
      }
    },
    parseFileNameAndExtension (fullFileName) {
      const name = fullFileName.slice(0, fullFileName.lastIndexOf('.')).trim()
      const extension = fullFileName.split('.').pop()
      return {
        name,
        extension
      }
    },
    getEmployeeDocuments () {
      this.isLoading = true
      this.axios.get(`/api/document/list_by/${this.employee.Employee_ID}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.documents = res.data
        this.isLoading = false
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    getEmployeeDocumentsTypes () {
      this.axios.get('/api/document_type', {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.documentsTypes = res.data
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    downloadDocument (item) {
      this.isLoading = true
      this.axios.get(`/api/document/${item.S3_Name}`, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        const contentType = res.data.ContentType
        const buffer = res.data.Body.data
        this.openFile(item.Document_Name, buffer, contentType)
        this.isLoading = false
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    openFile (filename, data, type) {
      const linkSource = this.linkToFile(data, type)
      this.downloadLink.href = linkSource
      this.downloadLink.download = filename
      this.downloadLink.click()
    },
    linkToFile (data, type) {
      let res = ''
      let binary = ''
      const bytes = new Uint8Array(data)
      const len = bytes.byteLength
      for (let i = 0; i < len; i++) {
        binary += String.fromCharCode(bytes[i])
      }
      res = `data:${type};base64, ${window.btoa(binary)}`
      return res
    },
    addNewDocument () {
      if (!this.addNewMode) {
        this.newDocument = {
          Document_Name: '',
          Document_Of_Employee_ID: this.employee.Employee_ID,
          Uploaded_By_Employee_ID: this.user.employee_id,
          File: null,
          Extension: '',
          Document_Type_ID: null,
          Uploaded_Date: '',
          Notes: ''
        }
        this.addNewMode = true
      } else {
        this.uploadDocument()
      }
    },
    onFileChangeAddMode () {
      if (this.newDocument.File) {
        const {
          name,
          extension
        } = this.parseFileNameAndExtension(this.newDocument.File.name)
        this.newDocument.Document_Name = name
        this.newDocument.Extension = extension
      } else {
        this.newDocument.Document_Name = ''
        this.newDocument.Extension = ''
        this.newDocument.File = null
      }
    },
    cancelAddNewDocument () {
      this.newDocument = null
      this.addNewMode = false
    },
    uploadDocument () {
      this.isLoading = true
      this.newDocument.Uploaded_Date = formatLocalDate()
      const formData = new FormData()
      this.newDocument.Document_Name = this.newDocument.Document_Name.trim()
      Object.entries(this.newDocument).forEach(([key, value]) => {
        formData.append(key, value)
      })

      this.axios.post('/api/document', formData, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.getEmployeeDocuments()
        this.cancelAddNewDocument()
        this.isLoading = false
        this.$store.dispatch('setSuccess', { successMsg: 'The document successfully uploaded' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    deleteDocument () {
      this.isLoading = true
      const body = {
        Deleted_Notes: this.deleteNotes
      }
      this.axios.put(`/api/document_soft_delete/${this.documentIDToDelete}`, body, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.cancelDeleteDocument()
        this.getEmployeeDocuments()
        this.isLoading = false
        this.$store.dispatch('setSuccess', { successMsg: 'The document successfully deleted' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    },
    onDeleteClick (item) {
      const date = new Date().toLocaleString()
      this.deleteDocumentDialog = true
      this.documentIDToDelete = item.Document_ID
      this.deleteNotes = `Deleted by: ${this.user.login}, Date: ${date}`
    },
    cancelDeleteDocument () {
      this.deleteNotes = ''
      this.documentIDToDelete = null
      this.deleteDocumentDialog = false
    },
    onEditClick (item, index) {
      if (!this.editMode) {
        this.allowTableSorting(false)
        this.editMode = true
        this.currentRowIndex = index
        this.documentOldName = item.Document_Name
        this.documentIDToEdit = item.Document_ID
        const documentTypeID = this.documentsTypes.find(el => el.Name === item.Type)

        this.editingDocument = {
          Document_Name: item.Document_Name,
          S3_Name: item.S3_Name,
          Location: item.Location,
          Updated_By_Employee_ID: this.user.employee_id,
          Uploaded_By_Employee_ID: item.Uploaded_By_Employee_ID,
          Document_Of_Employee_ID: this.employee.Employee_ID,
          Document_Type_ID: documentTypeID.Type_ID,
          Updated_Date: '',
          Uploaded_Date: item.Uploaded_Date,
          Notes: item.Notes,
          File: null,
          Extension: ''
        }
      } else {
        this.putEmployeeDocument()
      }
    },
    onFileChangeEditMode () {
      if (this.editingDocument.File) {
        const {
          name,
          extension
        } = this.parseFileNameAndExtension(this.editingDocument.File.name)
        this.editingDocument.Document_Name = name
        this.editingDocument.Extension = extension
        this.fileWasChangedEditMode = true
      } else {
        this.fileWasChangedEditMode = false
        this.editingDocument.File = null
        this.editingDocument.Document_Name = this.documentOldName
      }
    },
    cancelEditDocument () {
      this.allowTableSorting(true)
      this.editMode = false
      this.currentRowIndex = null
      this.editingDocument = null
      this.fileWasChangedEditMode = false
      this.documentOldName = null
      this.documentIDToEdit = null
    },
    putEmployeeDocument () {
      this.isLoading = true
      this.editingDocument.Updated_Date = formatLocalDate()
      this.editingDocument.Document_Name = this.editingDocument.Document_Name.trim()
      if (this.editingDocument.File) {
        this.editingDocument.Uploaded_Date = formatLocalDate()
        this.editingDocument.Uploaded_By_Employee_ID = this.user.employee_id
      }
      const formData = new FormData()
      Object.entries(this.editingDocument).forEach(([key, value]) => {
        formData.append(key, value)
      })
      this.axios.put(`/api/document/${this.documentIDToEdit}`, formData, {
        headers: {
          Authorization: 'Bearer ' + this.user.token
        }
      }).then(res => {
        this.cancelEditDocument()
        this.getEmployeeDocuments()
        this.isLoading = false
        this.$store.dispatch('setSuccess', { successMsg: 'The document successfully updated' })
      }).catch(error => {
        const errorMsg = getApiErrorMessage(error)
        this.$store.dispatch('setError', { errorMsg })
      })
    }
  }
}
</script>
